import moment from 'moment';

import { INTL_IDS } from './messages';

import {
  capitalizeString,
  convertNumToCurrencyStr,
  formatAmountsWithCommas,
  formatDate,
  formatFrequencyWithMonths,
  formatPercentage,
  maskedCharacterGenerator,
} from '../../utils/globalHelpers';
import { validate } from '../../utils/validate';
import {
  HELOC_DRAW_INELIGIBLE,
  HELOC_DRAW_INELIGIBLE_STATUS,
  HELOC_DRAW_PENDING,
  HELOC_DRAW_PENDING_STATUS,
  HELOC_DRAW_PROPS_DICTIONARY,
  INSURANCE_TYPE_DICT,
} from './constants';

export const formatEscrowData = ({ insuranceData, taxData }) => {
  const nowMoment = moment();
  const parsedTaxData = formatTaxData(taxData, nowMoment);
  const parsedInsuranceData = formatInsuranceData(insuranceData, nowMoment);
  return [...parsedTaxData, ...parsedInsuranceData];
};

export const formatInsuranceData = (insuranceData, nowMoment) => insuranceData.map(({
  annualPremiumAmount,
  companyName,
  insuranceType,
  policyExpirationDate,
  policyNumber,
  ...restInsuranceData
}) => {
  const isPast = moment(policyExpirationDate).isBefore(nowMoment);
  const formattedDate = formatDate(policyExpirationDate, null, 'MMMM D, YYYY');

  return {
    amount: formatAmountsWithCommas(annualPremiumAmount),
    bulletColor: isPast ? 'accentRed' : 'primary',
    companyName: capitalizeString(companyName),
    date: `${isPast ? 'Expired' : 'Expires'} ${formattedDate}`,
    insuranceType,
    policyNumber: maskedCharacterGenerator(policyNumber),
    type: INSURANCE_TYPE_DICT[insuranceType],
    ...restInsuranceData,
  };
});

export const formatMortgageInsuranceData = (data) => data.map(({
  companyName,
  monthlyPremiumAmount,
  nextDueDate,
  ...restMortgageInsuranceData
}) => ({
  amount: convertNumToCurrencyStr(monthlyPremiumAmount),
  companyName: capitalizeString(companyName),
  date: formatDate(nextDueDate, 'UTC', 'LL'),
  ...restMortgageInsuranceData,
}));

export const formatTaxData = (taxData, nowMoment) => {
  const unidentifiedTaxItems = taxData.filter(({ description }) => description === '').length;
  let unidentifiedTaxIndex = 0;

  return taxData.map(({
    description,
    identifier,
    installmentAmount,
    installmentDueDate,
    installmentFrequency,
    payeeName,
    ...restTaxData
  }) => {
    const isPast = moment(installmentDueDate).isBefore(nowMoment);
    const formattedDate = formatDate(installmentDueDate, null, 'MMMM YYYY');
    let type = capitalizeString(description);
    if (type === '') {
      unidentifiedTaxIndex += 1;
      type = unidentifiedTaxItems > 1 ? `Tax ${unidentifiedTaxIndex}` : 'Tax';
    }

    return {
      amount: formatAmountsWithCommas(installmentAmount),
      bulletColor: isPast ? 'accentGreen' : 'primary',
      date: formattedDate,
      // TODO: get Tax identifier from data source
      identifier: identifier || '',
      // TODO: get pay frequency from data source
      installmentFrequency: installmentFrequency || '',
      payeeName: capitalizeString(payeeName),
      type,
      ...restTaxData,
    };
  });
};

export const getElocBalance = (hasEloc, elocDetails) => {
  if (!hasEloc) return '';

  const { availableFunds } = elocDetails;
  const parsedAvailFunds = formatAmountsWithCommas(availableFunds) || 'N/A';

  return parsedAvailFunds;
};

export const getHelocDrawStatusBanner = (status) => {
  switch (status) {
    case HELOC_DRAW_INELIGIBLE_STATUS:
      return {
        message: HELOC_DRAW_PROPS_DICTIONARY.form.bannerText.ineligible,
        type: HELOC_DRAW_INELIGIBLE,
      };
    case HELOC_DRAW_PENDING_STATUS:
      return {
        message: HELOC_DRAW_PROPS_DICTIONARY.form.bannerText.alreadyRequested,
        type: HELOC_DRAW_PENDING,
      };
    default:
      return {};
  }
};

export const getSecondLoanBalance = (hasSecondLoan, summary) => {
  if (!hasSecondLoan) return '';

  const { principalBalance } = summary;
  const parsedPrinBal = formatAmountsWithCommas(principalBalance) || 'N/A';

  return parsedPrinBal;
};

const loanInfoFormatDictionary = {
  arm: {
    indexName: null,
    indexRate: formatPercentage,
    interestMaximumCeiling: formatPercentage,
    interestMaximumDecrease: formatPercentage,
    interestMaximumIncrease: formatPercentage,
    interestMinimumFloor: formatPercentage,
    interestRateChangeFrequency: formatFrequencyWithMonths,
    margin: formatPercentage,
    nextInterestRate: formatPercentage,
    nextPiPaymentAmount: formatAmountsWithCommas,
    nextScheduledInterestRateChangeDate: formatDate,
    nextScheduledPiChangeDate: formatDate,
    originalInterestRateChangeDate: formatDate,
    originalPiChangeDate: formatDate,
    paymentChangeFrequency: formatFrequencyWithMonths,
    roundingFactor: formatPercentage,
  },
  constructionLoan: {
    availableFunds: formatAmountsWithCommas,
    termExpirationDate: formatDate,
    totalLineAmount: formatAmountsWithCommas,
  },
  eLoc: {
    availableFunds: formatAmountsWithCommas,
    currentMinimumPaymentAmount: formatAmountsWithCommas,
    currrentInterestAmount: formatAmountsWithCommas,
    drawPeriodEndDate: formatDate,
    lastAdvanceDate: formatDate,
    maturityDate: formatDate,
    totalCredit: formatAmountsWithCommas,
    totalDueAmount: formatAmountsWithCommas,
  },
  secondLoan: {
    interestRate: formatPercentage,
    loanTerm: formatFrequencyWithMonths,
    loanType: null,
    originalMortgageAmount: formatAmountsWithCommas,
    principalAndInterest: formatAmountsWithCommas,
    principalBalance: formatAmountsWithCommas,
  },
  segmentedEloc: {
    availableFunds: formatAmountsWithCommas,
    currentInterestAmount: formatAmountsWithCommas,
    currentMinimumPaymentAmount: formatAmountsWithCommas,
    drawPeriodEndDate: formatDate,
    firstPaymentDueDate: formatDate,
    interestRate: formatPercentage,
    lastAdvanceDate: formatDate,
    maturityDate: formatDate,
    principalBalance: formatAmountsWithCommas,
    totalCredit: formatAmountsWithCommas,
  },
};

const loanInfoLabelDictionary = {
  arm: {
    indexName: INTL_IDS.MORTGAGE_DETAILS_LABEL_INDEXNAME,
    indexRate: INTL_IDS.MORTGAGE_DETAILS_LABEL_INDEXRATE,
    interestMaximumCeiling: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTMAXIMUMCEILING,
    interestMaximumDecrease: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTMAXIMUMDECREASE,
    interestMaximumIncrease: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTMAXIMUMINCREASE,
    interestMinimumFloor: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTMINIMUMFLOOR,
    interestRateChangeFrequency: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTRATECHANGEFREQUENCY,
    margin: INTL_IDS.MORTGAGE_DETAILS_LABEL_MARGIN,
    nextInterestRate: INTL_IDS.MORTGAGE_DETAILS_LABEL_NEXTINTERESTRATE,
    nextPiPaymentAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_NEXTPRINCIPALANDINTERESTPAYMENTAMOUNT,
    nextScheduledInterestRateChangeDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_NEXTSCHEDULEDINTERESTRATECHANGE,
    nextScheduledPiChangeDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_NEXTSCHEDULEDPRINCIPALANDINTERESTCHANGE,
    originalInterestRateChangeDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_ORIGINALINTERESTRATECHANGEDATE,
    originalPiChangeDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_ORIGINALPICHANGEDATE,
    paymentChangeFrequency: INTL_IDS.MORTGAGE_DETAILS_LABEL_PAYMENTCHANGEFREQUENCY,
    planId: INTL_IDS.MORTGAGE_DETAILS_LABEL_PLANID,
    roundingFactor: INTL_IDS.MORTGAGE_DETAILS_LABEL_ROUNDINGFACTOR,
  },
  constructionLoan: {
    availableFunds: INTL_IDS.MORTGAGE_DETAILS_LABEL_UNDISBURSEDFUNDS,
    termExpirationDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_TERMEXPIRATIONDATE,
    totalLineAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_LINEAMOUNT,
  },
  eLoc: {
    currentInterestAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_CURRENTINTERESTAMOUNT,
    currentMinimumPaymentAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_CURRENTMINIMUMPAYMENTAMOUNT,
    drawPeriodEndDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_DRAWPERIODENDDATE,
    lastAdvanceDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_LASTADVANCEDATE,
    maturityDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_MATURITYDATE,
    totalCredit: INTL_IDS.MORTGAGE_DETAILS_LABEL_CREDITLINEAMOUNT,
    totalDueAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_TOTALDUEAMOUNT,
  },
  secondLoan: {
    interestRate: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTRATE,
    loanTerm: INTL_IDS.MORTGAGE_DETAILS_LABEL_LOANTERM,
    loanType: INTL_IDS.MORTGAGE_DETAILS_LABEL_LOANTYPE,
    originalMortgageAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_ORIGINALMORTGAGEAMOUNT,
    principalAndInterest: INTL_IDS.MORTGAGE_DETAILS_LABEL_PRINCIPALANDINTEREST,
  },
  segmentedEloc: {
    availableFunds: INTL_IDS.MORTGAGE_DETAILS_LABEL_AVAILABLEADVANCEAMOUNT,
    currentInterestAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_INTERESTAMOUNT,
    currentMinimumPaymentAmount: INTL_IDS.MORTGAGE_DETAILS_LABEL_MINIMUMPAYMENTAMOUNT,
    description: INTL_IDS.MORTGAGE_DETAILS_LABEL_DESCRIPTION,
    drawPeriodEndDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_DRAWPERIODENDDATE,
    firstPaymentDueDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_FIRSTPAYMENTDUEDATE,
    interestRate: INTL_IDS.MORTGAGE_DETAILS_LABEL_ANNUALINTERESTRATE,
    lastAdvanceDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_LASTADVANCEDATE,
    maturityDate: INTL_IDS.MORTGAGE_DETAILS_LABEL_MATURITYDATE,
    principalBalance: INTL_IDS.MORTGAGE_DETAILS_LABEL_PRINCIPALBALANCE,
    segmentNumber: INTL_IDS.MORTGAGE_DETAILS_LABEL_SEGMENTNUMBER,
    segments: INTL_IDS.MORTGAGE_DETAILS_LABEL_SEGMENT,
    totalCredit: INTL_IDS.MORTGAGE_DETAILS_LABEL_TOTALCREDIT,
  },
};

/**
 * Formats order of eLoc segment loans as an array of key, value pairs
 * to preserve insertion order
 */
export const formatElocLoanSegments = (category, segment) => {
  const segmentedElocLoanRowOrder = [
    'currentInterestAmount',
    'currentMinimumPaymentAmount',
    'firstPaymentDueDate',
    'maturityDate',
    'principalBalance',
    'interestRate',
  ];
  return segmentedElocLoanRowOrder.map((prop) => {
    const formatValue = loanInfoFormatDictionary[category][prop];
    return {
      label: loanInfoLabelDictionary[category][prop],
      value: formatValue(segment[prop], 'UTC', 'LL'),
    };
  });
};

export const formatLoanTypeDetails = (
  category,
  details,
  isLoan,
  isMurabahaAccount,
) => {
  if (!isLoan) return false;
  const formattedDetails = Object.keys(details).reduce((acc, item) => {
    if (details[item] === null) return acc;
    if (item === 'principalBalance') return acc;
    if (
      item === 'availableFunds'
      && !['constructionLoan', 'segmentedEloc'].includes(category)
    ) return acc;

    const obj = {};
    obj.label = loanInfoLabelDictionary[category][item];

    const formatValue = loanInfoFormatDictionary[category][item];
    if (item === 'segments') {
      obj.value = details[item].map(({ description, segmentNumber, ...segment }) => {
        const segmentHeader = `Segment ${segmentNumber} - ${description}`;
        const segmentDetails = formatElocLoanSegments('segmentedEloc', segment);
        return { label: segmentHeader, value: segmentDetails };
      });
    } else if (formatValue) {
      obj.value = formatValue(details[item], 'UTC', 'LL');
    } else {
      obj.value = obj.label === 'mortgage.details.label.LoanType' && isMurabahaAccount
        ? moderateLoanType(details[item])
        : details[item];
    }
    acc.push(obj);

    return acc;
  }, []);
  return formattedDetails;
};

export const handleHelocFormValidation = ({
  canUseAnyRoutingNumber,
  currentAmount,
  dispatchUpdateFormError,
  formValues,
  maxHelocDrawAmount,
  minHelocDrawAmount,
}) => (
  Object.keys(formValues).reduce((isInputError, key) => {
    let validationProps;

    if (key === 'routingNumber' && !canUseAnyRoutingNumber) {
      return isInputError;
    }
    if (key === 'accountType') {
      return isInputError;
    }
    if (key === 'accountNumberConfirm') {
      validationProps = {
        comparisonValue: formValues.accountNumberConfirm,
        name: 'Account numbers',
        type: 'concurrentFields',
        value: formValues.accountNumber,
      };
    } else if (key === 'amount') {
      validationProps = {
        max: maxHelocDrawAmount,
        min: minHelocDrawAmount,
        type: 'helocDrawValue',
        value: currentAmount,
      };
    } else {
      validationProps = {
        type: key,
        value: formValues[key],
      };
    }

    const { message } = validate({
      ...validationProps,
      required: true,
    });

    if (message) {
      dispatchUpdateFormError({ field: key, form: 'helocDraw', value: message });
    }

    return message ? true : isInputError;
  }, false)
);

export const moderateLoanType = (loanType) => loanType.replace(/mortgage/gi, 'Murabaha Financing Transaction');
